import { z } from 'zod'

/** Second factor, configuration backup and the remote instance (PROMPT.md §7). */

export const totpStatusSchema = z.object({
  enabled: z.boolean(),
  /** How many unused recovery codes are left. Zero is worth a warning in the UI. */
  recoveryCodesLeft: z.number().int().nonnegative(),
})
export type TotpStatus = z.infer<typeof totpStatusSchema>

/**
 * The secret is handed out exactly once, while setting up. It is not stored anywhere on the
 * client — if the user closes the dialog before confirming, the setup starts over.
 */
export const totpSetupSchema = z.object({
  secret: z.string(),
  otpauthUri: z.string(),
  recoveryCodes: z.array(z.string()),
})
export type TotpSetup = z.infer<typeof totpSetupSchema>

export const totpConfirmSchema = z.object({
  secret: z.string().min(16),
  recoveryCodes: z.array(z.string().min(8)).min(1),
  /** Proof that the app was actually set up before the second factor becomes mandatory. */
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Der Code besteht aus sechs Ziffern.'),
})

export const totpDisableSchema = z.object({
  masterPassword: z.string().min(1, 'Bitte bestätige mit deinem Master-Passwort.'),
})

/** The address the desktop shell should show instead of its own backend, or null for local. */
export const remoteInstanceSchema = z.object({
  url: z
    .string()
    .trim()
    .max(2048)
    .nullable()
    .refine(
      (value) => value === null || isAllowedRemoteUrl(value),
      'Nur https-Adressen sind erlaubt (http nur für localhost).',
    ),
})
export type RemoteInstance = z.infer<typeof remoteInstanceSchema>

/**
 * A remote instance carries the master password over the wire, so plain http is refused —
 * the same rule §6.6 applies to the web variant itself. Loopback is the exception, because
 * there is no network to listen on.
 */
export function isAllowedRemoteUrl(value: string): boolean {
  let url: URL
  try {
    url = new URL(value)
  } catch {
    return false
  }
  if (url.protocol === 'https:') return true
  if (url.protocol !== 'http:') return false
  return url.hostname === 'localhost' || url.hostname === '127.0.0.1' || url.hostname === '[::1]'
}

export const configImportResultSchema = z.object({
  /** Where the replaced files were kept, so a wrong import can be undone by hand. */
  previousDatabase: z.string().nullable(),
  previousVault: z.string().nullable(),
  vaultReplaced: z.boolean(),
  createdAt: z.string(),
  appVersion: z.string(),
})
export type ConfigImportResult = z.infer<typeof configImportResultSchema>
